/**
 * JobsFilterDrawer — the right-hand slide-over holding every filter on the
 * Ops lens of the jobs list (WorkPortfolioPage). Replaces the page's
 * previously accreted chip/pill/stat-card widgets with one catalogue,
 * grouped into five collapsible sections matching the questions a CSR
 * actually asks (who owns it / what's wrong with it / where is it / when is
 * it due / what kind of work). See
 * docs/features/jobs-filter-system/01_product_brief.md for the full
 * rationale; `TicketsPage`'s slide-over is the structural precedent this
 * follows (checkbox-group multi-select, "N filters" trigger owned by the
 * page, "Clear all" separate).
 *
 * All filter *values* live in the page (`JobsFilterValues`, URL-synced);
 * this component is a thin, stateless-except-for-section-collapse renderer
 * over `filters`/`onChange`.
 */
import { useEffect, useState } from "react";
import { CalendarIcon, ChevronDown } from "lucide-react";
import { format } from "date-fns";
import type { DateRange } from "react-day-picker";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  WorkPortfolioDueWindow,
  WorkPortfolioQuantityBand,
  WorkPortfolioRepeatFlag,
  WorkPortfolioValueBand,
} from "@shared-core/data/adapter";
import type { JobsFilterValues } from "@shared-core/domain/workPortfolioFilters";
import {
  READINESS_LADDER,
  readinessDisplayLabel,
} from "@shared-core/domain/readiness";
import {
  STANDARD_STAGES,
  WORK_PORTFOLIO_CSR_OPTIONS,
  WORK_PORTFOLIO_DESPATCH_METHOD_OPTIONS,
  WORK_PORTFOLIO_JOB_TYPE_OPTIONS,
  WORK_PORTFOLIO_MARKET_OPTIONS,
  WORK_PORTFOLIO_PRODUCTION_SITE_OPTIONS,
  WORK_PORTFOLIO_SALES_REP_OPTIONS,
} from "@shared-core/mock-data/work-visibility";
import { SlideOverPanel } from "../../patterns/SlideOverPanel";
import { FilterCheckboxGroup } from "../../patterns/FilterCheckboxGroup";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../primitives/collapsible";
import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";
import { Calendar } from "../../primitives/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../primitives/popover";
import {
  DateRangePicker,
  type DateRangePreset,
} from "../../patterns/DateRangePicker";

// Exact labels from the seeded work_visibility_exception_rules.display_name
// values (jobs-filter-system brief §19.1 — "stage" is the agreed term for
// the UI-facing word; the underlying rule codes are unchanged).
export const ALERT_REASON_OPTIONS: { value: string; label: string }[] = [
  { value: "overdue_terminal_deadline", label: "Past due date" },
  { value: "approaching_terminal_deadline", label: "Due date approaching" },
  { value: "phase_deadline_at_risk", label: "Stage running late" },
  {
    value: "phase_downstream_blocked",
    label: "Blocked by an earlier stage",
  },
  { value: "issue_raised", label: "Issue raised against job" },
];

export const JOB_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "in_progress", label: "In progress" },
  { value: "produced", label: "Produced" },
  { value: "delivered", label: "Delivered" },
  { value: "invoiced", label: "Invoiced" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

export const VALUE_BAND_OPTIONS: {
  value: WorkPortfolioValueBand;
  label: string;
}[] = [
  { value: "under_250", label: "Under £250" },
  { value: "250_1000", label: "£250 – £1,000" },
  { value: "1000_5000", label: "£1,000 – £5,000" },
  { value: "5000_plus", label: "£5,000+" },
];

export const QUANTITY_BAND_OPTIONS: {
  value: WorkPortfolioQuantityBand;
  label: string;
}[] = [
  { value: "under_500", label: "Under 500" },
  { value: "500_2000", label: "500 – 2,000" },
  { value: "2000_10000", label: "2,000 – 10,000" },
  { value: "10000_plus", label: "10,000+" },
];

export const DUE_WINDOW_OPTIONS: {
  value: WorkPortfolioDueWindow;
  label: string;
}[] = [
  { value: "overdue", label: "Overdue" },
  { value: "due_today", label: "Due today" },
  // Matches the CSR-home "Due soon" lane's own definition (today, exclusive,
  // through today + 3 working days) — not a calendar window like the other
  // presets here, so it is kept distinct from "Due this week".
  { value: "due_soon", label: "Due soon" },
  { value: "due_this_week", label: "Due this week" },
  { value: "due_two_weeks", label: "Due in 2 weeks" },
  { value: "custom", label: "Custom range" },
];

export function alertReasonLabel(code: string): string {
  return ALERT_REASON_OPTIONS.find((o) => o.value === code)?.label ?? code;
}

export function jobStatusLabel(code: string): string {
  return JOB_STATUS_OPTIONS.find((o) => o.value === code)?.label ?? code;
}

export function stageLabel(code: string): string {
  return STANDARD_STAGES.find((p) => p.code === code)?.display ?? code;
}

export function dueWindowLabel(window: WorkPortfolioDueWindow): string {
  return DUE_WINDOW_OPTIONS.find((o) => o.value === window)?.label ?? window;
}

export function valueBandLabel(band: WorkPortfolioValueBand): string {
  return VALUE_BAND_OPTIONS.find((o) => o.value === band)?.label ?? band;
}

export function quantityBandLabel(band: WorkPortfolioQuantityBand): string {
  return QUANTITY_BAND_OPTIONS.find((o) => o.value === band)?.label ?? band;
}

// A single checkbox row outside a FilterCheckboxGroup — "Mine", "Only
// unacknowledged alerts", "Has open issues", etc. Same visual language as
// the group's own rows, one at a time.
function SingleCheckboxRow({
  label,
  checked,
  onChange,
  disabled = false,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label
      className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-sm ${
        disabled
          ? "cursor-not-allowed text-muted-foreground/60"
          : "cursor-pointer hover:bg-accent/50"
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="accent-primary"
      />
      {label}
    </label>
  );
}

// A job has exactly one readiness value, one production stage, and one due
// window — single-select "chip" behaviour (click to select, click again to
// clear) rather than a checkbox group, and matches the real backend's
// scalar query params for readiness/stage_code.
function SingleSelectChips<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: T | "";
  onChange: (value: T | "") => void;
  options: { value: T; label: string }[];
}) {
  return (
    <div>
      <div className="text-xs font-medium text-muted-foreground uppercase mb-2">
        {label}
      </div>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const active = value === option.value;
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={active}
              onClick={() => onChange(active ? "" : option.value)}
              className={`px-3 py-1 rounded text-sm transition-colors ${
                active
                  ? "bg-[color:var(--color-primary)] text-white"
                  : "bg-muted text-muted-foreground hover:text-foreground"
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// One collapsible section of the drawer. Starts expanded when it already
// has an active filter, collapsed otherwise — the useState initial value
// intentionally only reflects mount-time state; the section doesn't jump
// open/closed under the user as they check/uncheck boxes.
function FilterSection({
  title,
  activeCount,
  children,
}: {
  title: string;
  activeCount: number;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(activeCount > 0);
  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="border-b border-border pb-4 last:border-0 last:pb-0"
    >
      <CollapsibleTrigger className="flex w-full items-center justify-between py-1 text-sm font-semibold">
        <span className="flex items-center gap-2">
          {title}
          {activeCount > 0 && (
            <Badge variant="secondary" className="h-5 min-w-5 px-1.5 text-xs">
              {activeCount}
            </Badge>
          )}
        </span>
        <ChevronDown
          className={`size-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-4 pt-3">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}

function toIsoDate(d: Date | undefined): string {
  return d ? format(d, "yyyy-MM-dd") : "";
}

// Custom due-date range — a Popover + range Calendar, the same mechanics
// DateRangePicker uses for its own "custom" branch, but without that
// component's backward-looking presets (7d/30d/90d/all), which don't fit a
// forward-looking "when is it due" question.
function DueCustomRangePicker({
  dueAfter,
  dueBefore,
  onChange,
}: {
  dueAfter: string;
  dueBefore: string;
  onChange: (dueAfter: string, dueBefore: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const range: DateRange | undefined =
    dueAfter || dueBefore
      ? {
          from: dueAfter ? new Date(dueAfter) : undefined,
          to: dueBefore ? new Date(dueBefore) : undefined,
        }
      : undefined;
  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="justify-start text-left font-normal"
        >
          <CalendarIcon className="mr-2 size-4" />
          {dueAfter || dueBefore
            ? `${dueAfter || "…"} – ${dueBefore || "…"}`
            : "Pick a due-date range"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          autoFocus
          mode="range"
          selected={range}
          onSelect={(next) =>
            onChange(toIsoDate(next?.from), toIsoDate(next?.to))
          }
          numberOfMonths={2}
        />
      </PopoverContent>
    </Popover>
  );
}

export interface JobsFilterDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: JobsFilterValues;
  onChange: (patch: Partial<JobsFilterValues>) => void;
  onClearAll: () => void;
  activeFilterCount: number;
  /** Gates the "Viewing as" colleague picker — only sales_rep/csr roles may
   *  cover a colleague (see `isScopeOverrideRole`). */
  canViewAsColleague: boolean;
  colleagues: { id: string; name: string }[];
  /** Sort moved in from the page's old inline `<select>` — it isn't a
   *  filter axis, so it renders above the five catalogue sections rather
   *  than inside one of them. Kept as plain strings so this component
   *  doesn't need to import the page's `SortOption` type. */
  sortValue: string;
  sortOptions: { value: string; label: string }[];
  onSortChange: (value: string) => void;
}

export function JobsFilterDrawer({
  open,
  onOpenChange,
  filters,
  onChange,
  onClearAll,
  activeFilterCount,
  canViewAsColleague,
  colleagues,
  sortValue,
  sortOptions,
  onSortChange,
}: JobsFilterDrawerProps) {
  const [bookedPreset, setBookedPreset] = useState<DateRangePreset>(
    filters.bookedAfter || filters.bookedBefore ? "custom" : "30d",
  );
  // "Viewing as" already answers whose work is being shown, so ANDing it
  // with "Mine" (my own portfolio/bookings) would intersect two different
  // people's scopes into something meaningless. Covers both the interactive
  // case (the select's own onChange below also clears `mine` immediately)
  // and a deep link that already carries both params — either way the
  // control renders unchecked+disabled and the URL never carries a stale
  // `mine=1` alongside `viewing_as`.
  const viewingAsColleague =
    canViewAsColleague && Boolean(filters.viewingAsUserId);
  useEffect(() => {
    if (viewingAsColleague && filters.mine) onChange({ mine: false });
  }, [viewingAsColleague, filters.mine, onChange]);
  const viewingAsName = colleagues.find(
    (c) => c.id === filters.viewingAsUserId,
  )?.name;
  const bookedRange: DateRange | undefined =
    filters.bookedAfter || filters.bookedBefore
      ? {
          from: filters.bookedAfter ? new Date(filters.bookedAfter) : undefined,
          to: filters.bookedBefore ? new Date(filters.bookedBefore) : undefined,
        }
      : undefined;

  return (
    <SlideOverPanel
      open={open}
      onOpenChange={onOpenChange}
      title="Filters"
      footer={
        activeFilterCount > 0 ? (
          <button
            onClick={onClearAll}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            Clear all
          </button>
        ) : undefined
      }
    >
      <div className="space-y-5">
        <div>
          <div className="text-xs font-medium text-muted-foreground uppercase mb-2">
            Sort by
          </div>
          <select
            value={sortValue}
            onChange={(e) => onSortChange(e.target.value)}
            className="w-full px-3 py-1.5 border border-border rounded bg-background text-sm"
          >
            {sortOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <FilterSection
          title="Who owns it"
          activeCount={
            (filters.mine ? 1 : 0) +
            (filters.viewingAsUserId ? 1 : 0) +
            filters.csr.length +
            filters.salesRep.length
          }
        >
          <SingleCheckboxRow
            label="Mine — my customers, or jobs I booked"
            checked={filters.mine}
            onChange={(checked) => onChange({ mine: checked })}
            disabled={viewingAsColleague}
          />
          {viewingAsColleague && (
            <p className="text-xs text-muted-foreground px-2 -mt-2">
              You're already viewing {viewingAsName ?? "a colleague"}'s work, so
              "Mine" is turned off — clear "Viewing as" below to see your own
              portfolio again.
            </p>
          )}
          {canViewAsColleague && (
            <div>
              <div className="text-xs font-medium text-muted-foreground uppercase mb-2">
                Viewing as
              </div>
              <select
                aria-label="Viewing as"
                value={filters.viewingAsUserId}
                onChange={(e) => {
                  const viewingAsUserId = e.target.value;
                  onChange(
                    viewingAsUserId
                      ? { viewingAsUserId, mine: false }
                      : { viewingAsUserId },
                  );
                }}
                className="w-full px-3 py-1.5 border border-border rounded bg-background text-sm"
              >
                <option value="">My own view</option>
                {colleagues.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <FilterCheckboxGroup
            label="CSR"
            value={filters.csr}
            onChange={(csr) => onChange({ csr })}
            options={WORK_PORTFOLIO_CSR_OPTIONS.map((v) => ({
              value: v,
              label: v,
            }))}
          />
          <FilterCheckboxGroup
            label="Sales rep"
            value={filters.salesRep}
            onChange={(salesRep) => onChange({ salesRep })}
            options={WORK_PORTFOLIO_SALES_REP_OPTIONS.map((v) => ({
              value: v,
              label: v,
            }))}
          />
        </FilterSection>

        <FilterSection
          title="What's wrong with it"
          activeCount={
            filters.exceptionCodes.length +
            (filters.unacknowledgedOnly ? 1 : 0) +
            (filters.hasIssues ? 1 : 0) +
            (filters.complaints ? 1 : 0) +
            (filters.alertsOnly ? 1 : 0)
          }
        >
          <SingleCheckboxRow
            label="Only jobs with alerts"
            checked={filters.alertsOnly}
            onChange={(checked) => onChange({ alertsOnly: checked })}
          />
          <FilterCheckboxGroup
            label="Alert reason"
            value={filters.exceptionCodes}
            onChange={(exceptionCodes) => onChange({ exceptionCodes })}
            options={ALERT_REASON_OPTIONS}
          />
          <SingleCheckboxRow
            label="Only unacknowledged alerts"
            checked={filters.unacknowledgedOnly}
            onChange={(checked) => onChange({ unacknowledgedOnly: checked })}
          />
          <SingleCheckboxRow
            label="Has open issues"
            checked={filters.hasIssues}
            onChange={(checked) => onChange({ hasIssues: checked })}
          />
          <SingleCheckboxRow
            label="Has complaints"
            checked={filters.complaints}
            onChange={(checked) => onChange({ complaints: checked })}
          />
        </FilterSection>

        <FilterSection
          title="Where it is"
          activeCount={
            (filters.readiness ? 1 : 0) +
            (filters.stageCode ? 1 : 0) +
            filters.statuses.length
          }
        >
          <SingleSelectChips
            label="Readiness"
            value={filters.readiness}
            onChange={(readiness) => onChange({ readiness })}
            options={READINESS_LADDER.map((r) => ({
              value: r,
              label: readinessDisplayLabel(r),
            }))}
          />
          <SingleSelectChips
            label="Production stage"
            value={filters.stageCode}
            onChange={(stageCode) => onChange({ stageCode })}
            options={STANDARD_STAGES.map((p) => ({
              value: p.code,
              label: p.display,
            }))}
          />
          <FilterCheckboxGroup
            label="Job status"
            value={filters.statuses}
            onChange={(statuses) => onChange({ statuses })}
            options={JOB_STATUS_OPTIONS}
          />
        </FilterSection>

        <FilterSection
          title="When it's due"
          activeCount={
            (filters.dueWindow ? 1 : 0) +
            (filters.bookedAfter || filters.bookedBefore ? 1 : 0) +
            (filters.waitingMoreThanDays ? 1 : 0)
          }
        >
          <SingleSelectChips
            label="Due"
            value={filters.dueWindow}
            onChange={(dueWindow) => onChange({ dueWindow })}
            options={DUE_WINDOW_OPTIONS}
          />
          {filters.dueWindow === "custom" && (
            <DueCustomRangePicker
              dueAfter={filters.dueAfter}
              dueBefore={filters.dueBefore}
              onChange={(dueAfter, dueBefore) =>
                onChange({ dueAfter, dueBefore })
              }
            />
          )}
          <div>
            <div className="text-xs font-medium text-muted-foreground uppercase mb-2 flex items-center justify-between">
              <span>Booked</span>
              {(filters.bookedAfter || filters.bookedBefore) && (
                <button
                  type="button"
                  onClick={() => {
                    setBookedPreset("30d");
                    onChange({ bookedAfter: "", bookedBefore: "" });
                  }}
                  className="text-muted-foreground hover:text-foreground normal-case font-normal"
                >
                  Clear
                </button>
              )}
            </div>
            <DateRangePicker
              date={bookedRange}
              preset={bookedPreset}
              onPresetChange={setBookedPreset}
              onDateChange={(range) =>
                onChange({
                  bookedAfter: toIsoDate(range?.from),
                  bookedBefore: toIsoDate(range?.to),
                })
              }
              className="w-full"
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground whitespace-nowrap">
              Waiting more than
            </span>
            <input
              type="number"
              min={1}
              value={filters.waitingMoreThanDays}
              onChange={(e) =>
                onChange({ waitingMoreThanDays: e.target.value })
              }
              className="w-16 px-2 py-1 border border-border rounded bg-background text-sm"
            />
            <span className="text-muted-foreground">days</span>
          </label>
        </FilterSection>

        <FilterSection
          title="What kind of work"
          activeCount={
            filters.jobTypeCode.length +
            filters.repeatFlags.length +
            filters.productionSite.length +
            filters.despatchMethod.length +
            filters.market.length +
            filters.valueBand.length +
            filters.quantityBand.length +
            (filters.inSeries ? 1 : 0)
          }
        >
          <FilterCheckboxGroup
            label="Product / job type"
            value={filters.jobTypeCode}
            onChange={(jobTypeCode) => onChange({ jobTypeCode })}
            options={WORK_PORTFOLIO_JOB_TYPE_OPTIONS.map((v) => ({
              value: v,
              label: v,
            }))}
          />
          <FilterCheckboxGroup<WorkPortfolioRepeatFlag>
            label="New or repeat"
            value={filters.repeatFlags}
            onChange={(repeatFlags) => onChange({ repeatFlags })}
            options={[
              { value: "new", label: "New work" },
              { value: "repeat", label: "Repeat work" },
            ]}
          />
          <FilterCheckboxGroup
            label="Production site"
            value={filters.productionSite}
            onChange={(productionSite) => onChange({ productionSite })}
            options={WORK_PORTFOLIO_PRODUCTION_SITE_OPTIONS.map((v) => ({
              value: v,
              label: v,
            }))}
          />
          <FilterCheckboxGroup
            label="Despatch method"
            value={filters.despatchMethod}
            onChange={(despatchMethod) => onChange({ despatchMethod })}
            options={WORK_PORTFOLIO_DESPATCH_METHOD_OPTIONS.map((v) => ({
              value: v,
              label: v,
            }))}
          />
          <FilterCheckboxGroup
            label="Market"
            value={filters.market}
            onChange={(market) => onChange({ market })}
            options={WORK_PORTFOLIO_MARKET_OPTIONS.map((v) => ({
              value: v,
              label: v,
            }))}
          />
          <FilterCheckboxGroup<WorkPortfolioValueBand>
            label="Value"
            value={filters.valueBand}
            onChange={(valueBand) => onChange({ valueBand })}
            options={VALUE_BAND_OPTIONS}
          />
          <FilterCheckboxGroup<WorkPortfolioQuantityBand>
            label="Quantity"
            value={filters.quantityBand}
            onChange={(quantityBand) => onChange({ quantityBand })}
            options={QUANTITY_BAND_OPTIONS}
          />
          <SingleCheckboxRow
            label="Part of a series"
            checked={filters.inSeries}
            onChange={(checked) => onChange({ inSeries: checked })}
          />
        </FilterSection>
      </div>
    </SlideOverPanel>
  );
}

/** Fetches the colleague roster for the "Viewing as" picker, scoped to the
 *  scope-override-eligible roles — same surface and narrowing as
 *  CustomersPage's colleague dropdown. Lives here (not inside the drawer)
 *  because the page also needs the resolved name for the active-filter
 *  chip label. */
export function useWorkPortfolioColleagues(enabled: boolean) {
  const dataAdapter = useDataAdapter();
  const [colleagues, setColleagues] = useState<{ id: string; name: string }[]>(
    [],
  );
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    dataAdapter
      .listUsers({ roles: ["sales_rep", "csr"] })
      .then((page) => {
        if (!cancelled) {
          setColleagues(page.items.map((u) => ({ id: u.id, name: u.name })));
        }
      })
      .catch((error) => {
        console.error("Error loading colleagues:", error);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, enabled]);
  return colleagues;
}
